/**
 * **La chute** — game over en mode normal (SPEC-API §7.3, M2.7).
 *
 * En normal, une fin ne ferme pas la run : on repart du dernier palier franchi, les Clicks
 * divisés par deux, la santé mentale à 60 et l'énergie pleine. Jusqu'à `e61f48a` côté serveur, ce
 * moment était **invisible** : le `state` renvoyé étant déjà restauré, `status` valait `active` et
 * aucun écran ne se déclenchait. Le joueur perdait la moitié de ses Clicks sans qu'on le lui dise.
 *
 * **Écran plein, pas un bandeau** ✎ — c'est le moment le plus dramatique du jeu, et un bandeau
 * qui disparaît en quatre secondes ne peut pas le porter. Si Mathieu le trouve trop lourd, le
 * repli est mécanique : ce composant devient le corps d'un `dialog.info` sans rien changer
 * d'autre.
 *
 * L'écran **raconte** au lieu de constater — c'est à ça que servent les `avant`/`apres` du
 * contrat : « tu avais 1 840 abonnés au jour 142, tu repars du palier des 1 000 ».
 *
 * ⚠️ Une chose à ne jamais écrire ici : « tu as perdu des compétences » : **aucun nœud n'est repris**
 * (arbitrage A50).
 *
 * ⟦C48 · A-168 §3⟧ **Dans la DA** : une `Fenetre` sur le fond de la chambre (`EcranFenetre.tsx`),
 * titre de la cause et badge « tu n'es pas éliminé » dans le bandeau, bouton dans le pied fixe.
 * Ni un mot ni un chiffre n'ont changé.
 *
 * ⟦C57 · lot 7 · A-174⟧ **Moins de texte** (Matt, 02/10 : une chute au jalon suit la règle normale,
 * seules les FINS gardent leur narration). L'écran garde le titre de la cause, le bilan en chiffres
 * (abonnés, argent, réputation) et UNE phrase de 12 mots au plus : où l'on repart, et combien de
 * jours ça efface (`phraseDeReprise`, `game/fins.ts`). Le récit de la cause et la REPRISE au jalon
 * entière (« aucune compétence n'est reprise ») s'ouvrent par « Les dessous », mot pour mot.
 *
 * ⟦C60 · A-177⟧ **Le grand ménage : une chute ne parle plus que de santé mentale et d'énergie.** Il
 * ne reste qu'une cause de chute, le burn-out (la faillite, les strikes et la spirale de réputation
 * ne tombent plus : voir `gameOversPossibles`, `game/fins.ts`). Le bilan garde les abonnés et dit où
 * en sont la santé mentale et l'énergie après la reprise (lues sur l'état restauré, jamais
 * recopiées) ; la ligne de Clicks et la ligne de réputation en sortent. Les Clicks divisés par deux
 * restent dits, derrière « Les dessous », puisque c'est ce qui paie le Marché et l'Équipe. « Le
 * jalon » devient « le palier » partout (les vidéos jalons n'existent plus). Les noms du fichier, du
 * composant et du type `RetourAuJalon` ne bougent pas : c'est le contrat du serveur
 * (`retour_au_jalon`), et `App.tsx` les importe.
 */

import { DessousDuJournal } from "../../components/DessousDuJournal";
import { EcranFenetre } from "../../components/EcranFenetre";
import { phraseDeReprise, texteClicksDeReprise, texteDeReprise } from "../../game/fins";
import type { RetourAuJalon } from "../../game/gameClient";
import { nombre } from "../../game/format";
import { detailJournal } from "../../game/journal";
import { interrupteurs } from "../../game/menage";
import { useRunStore } from "../../stores/runStore";

/**
 * Une ligne du bilan. Le sens de la flèche n'est **pas** toujours vers le bas : le retour au
 * dernier palier restaure un état PASSÉ, donc une santé mentale à zéro remonte à 60 et l'énergie
 * à fond. Barrer ces valeurs en rouge comme des pertes raconterait le contraire de ce qui se passe.
 */
function Ligne({ avant, apres, monte }: { avant: string; apres: string; monte: boolean }) {
  return (
    <div className={`chute-ligne ${monte ? "monte" : ""}`}>
      <span className="chute-avant">{avant}</span>
      <span className="chute-fleche" aria-hidden="true">
        {monte ? "↑" : "↓"}
      </span>
      <span className="chute-apres">{apres}</span>
    </div>
  );
}

/**
 * Ce que la cause raconte au joueur. ⟦C60 · A-177⟧ Une seule cause : le burn-out. Les trois autres
 * (`reputation`, `ban`, `bankruptcy`) ne tombent plus ; leur récit vit dans `RECITS_DE_FIN_ANCIENS`
 * (`game/fins.ts`), et une cause inconnue retombe sur le repli neutre plus bas.
 */
const CAUSES: Record<string, { titre: string; texte: string; icone: string }> = {
  burnout: {
    icone: "🫠",
    titre: "Tu t'es effondré",
    texte:
      "La deuxième crise a été la bonne. Tu t'es arrêté net, et pendant ce temps la chaîne a continué sans toi, mal.",
  },
};

export function ChuteAuJalon({
  retour,
  nomDeChaine,
  onContinuer,
}: {
  retour: RetourAuJalon;
  nomDeChaine: string;
  onContinuer(): void;
}) {
  // ⟦C60⟧ L'état d'APRÈS la chute : c'est lui qui dit où en sont la santé mentale et l'énergie.
  const etat = useRunStore((s) => s.run?.state ?? null);
  const cause = CAUSES[retour.cause] ?? {
    icone: "💥",
    titre: "Ça s'est effondré",
    texte: "La chaîne est repartie plus bas.",
  };

  // ⟦C57 · lot 7⟧ Ce que l'écran ne dit plus, mot pour mot : le récit de la cause, puis la reprise
  // (`texteDeReprise`, `game/fins.ts`). Les passages en gras d'avant (« repart de zéro », « aucune
  // compétence n'est reprise ») sont du texte courant dans le dialogue. ⟦C60⟧ Les Clicks divisés par
  // deux s'y ajoutent (ils ne sont plus une ligne du bilan).
  const texteDeClicks =
    retour.argent_apres > 0 ? texteClicksDeReprise(retour.argent_apres, interrupteurs().reprise_clicks) : null;
  const detail = detailJournal("La chute, en détail", [
    { titre: "Ce qui s'est passé", paragraphes: [cause.texte] },
    { titre: "La reprise", paragraphes: [texteDeReprise(nomDeChaine, retour)] },
    // ⟦C60 bis · A-177⟧ La chute ne retire plus de Clicks : à 100 % gardés, rien à dire de ce côté-là.
    texteDeClicks !== null ? { titre: "Les Clicks", paragraphes: [texteDeClicks] } : null,
  ]);

  return (
    <EcranFenetre
      titre={cause.titre}
      icone={cause.icone}
      badge="tu n'es pas éliminé"
      className="fenetre-chute"
      pied={
        <button className="bouton primaire grand" type="button" onClick={onContinuer}>
          Reprendre là
        </button>
      }
    >
      <div className="chute-bilan">
        <Ligne
          avant={`${nombre(retour.subs_avant)} abonnés au jour ${retour.jour_avant}`}
          apres={`${nombre(retour.subs_apres)} abonnés`}
          monte={retour.subs_apres > retour.subs_avant}
        />
        {etat ? (
          <Ligne
            avant="santé mentale à zéro"
            apres={`❤ ${Math.round(etat.mental)} · ⚡ ${Math.round(etat.energy)}`}
            monte
          />
        ) : null}
      </div>

      <p className="chute-reprise">{phraseDeReprise(retour)}</p>

      {detail ? (
        <div className="dessous-pose">
          <DessousDuJournal detail={detail} variante="texte" />
        </div>
      ) : null}
    </EcranFenetre>
  );
}
